<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

interface Props {
  windPower: number | null | undefined;  // 風力發電功率 W
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false
});

// 格式化數值
const formattedValue = computed(() => {
  if (props.windPower === null || props.windPower === undefined) return '0';
  return props.windPower.toFixed(1);
});

// 計算發電等級
const powerLevel = computed(() => {
  const value = props.windPower;
  if (value === null || value === undefined) return 'none';
  if (value === 0) return 'stopped';
  if (value < 50) return 'low';
  if (value < 200) return 'medium';
  if (value < 500) return 'high';
  return 'very-high';
});

// 等級文字
const powerLevelText = computed(() => {
  switch (powerLevel.value) {
    case 'stopped': return t('dashboard.weather.windStopped');
    case 'low': return t('dashboard.weather.windLow');
    case 'medium': return t('dashboard.weather.windMedium');
    case 'high': return t('dashboard.weather.windHigh');
    case 'very-high': return t('dashboard.weather.windVeryHigh');
    default: return '-';
  }
});

// 是否正在發電
const isGenerating = computed(() => {
  return props.windPower !== null && props.windPower !== undefined && props.windPower > 0;
});
</script>

<template>
  <div class="power-card wind-card">
    <div class="card-header">
      <div class="card-icon">🌀</div>
      <h3>{{ t('dashboard.weather.windPower') }}</h3>
    </div>
    <div class="card-value">
      {{ formattedValue }}<span class="unit">W</span>
    </div>
    <div v-if="windPower !== null && windPower !== undefined"
         class="efficiency"
         :class="'level-' + powerLevel">
      {{ powerLevelText }}
    </div>
    <div class="card-footer">
      <span class="status-dot" :class="{ active: isGenerating }"></span>
      {{ isGenerating ? t('dashboard.weather.generating') : t('dashboard.weather.standby') }}
    </div>
  </div>
</template>

<style scoped>
.power-card {
  background: rgba(255, 255, 255, 0.08);
  padding: 25px;
  border-radius: 10px;
  border: 2px solid transparent;
  transition: all 0.3s;
  cursor: pointer;
}

.power-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
}

.wind-card {
  border-color: #9C27B0;
}

.wind-card:hover {
  background: rgba(156, 39, 176, 0.1);
  box-shadow: 0 10px 25px rgba(156, 39, 176, 0.3);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 15px;
}

.card-icon {
  font-size: 32px;
}

.card-header h3 {
  font-size: 16px;
  color: #b0bec5;
  font-weight: 600;
  margin: 0;
}

.card-value {
  font-size: 48px;
  font-weight: bold;
  color: #ecf0f1;
  line-height: 1.2;
}

.card-value .unit {
  font-size: 18px;
  font-weight: normal;
  color: #90a4ae;
  margin-left: 8px;
}

.efficiency {
  font-size: 14px;
  padding: 6px 12px;
  border-radius: 6px;
  text-align: center;
  margin-top: 10px;
  display: inline-block;
}

.level-stopped {
  background: rgba(158, 158, 158, 0.2);
  color: #9E9E9E;
}

.level-low {
  background: rgba(33, 150, 243, 0.2);
  color: #2196F3;
}

.level-medium {
  background: rgba(156, 39, 176, 0.2);
  color: #9C27B0;
}

.level-high {
  background: rgba(103, 58, 183, 0.2);
  color: #673AB7;
}

.level-very-high {
  background: rgba(63, 81, 181, 0.2);
  color: #3F51B5;
}

.card-footer {
  font-size: 12px;
  color: #78909c;
  margin-top: 10px;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #78909c;
}

.status-dot.active {
  background: #4CAF50;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
